import React, { useState } from "react";
import axios from "axios";
import { MapPin, Clock, Phone, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import { useReveal } from "@/hooks/useReveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  subject: "",
  area: "altro",
  message: "",
};

const Contact = () => {
  const { t, lang } = useLanguage();
  const { ref, visible } = useReveal();
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error(t.contact.form.validation);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.contact.form.success);
      setForm(initial);
    } catch (err) {
      console.error(err);
      toast.error(t.contact.form.error);
    } finally {
      setLoading(false);
    }
  };

  const areas = t.contact.form.area_options;

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-28 md:py-40 border-t border-white/5"
    >
      <div
        ref={ref}
        className={`max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-14 reveal ${
          visible ? "is-visible" : ""
        }`}
      >
        {/* Left info */}
        <div className="md:col-span-5">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-6 h-[1px] bg-[var(--ps-gold)]" />
            <span className="text-xs tracking-[0.3em] text-[var(--ps-gold)]">
              {t.contact.kicker}
            </span>
          </div>
          <h2
            data-testid="contact-title"
            className="font-serif-display text-5xl md:text-7xl leading-[0.98] text-white mb-6"
          >
            {t.contact.title.split(" ")[0]}{" "}
            <span className="italic text-[var(--ps-gold)]">
              {t.contact.title.split(" ").slice(1).join(" ")}
            </span>
          </h2>
          <p className="text-base text-zinc-400 leading-relaxed max-w-lg mb-14">
            {t.contact.subtitle}
          </p>

          <div className="space-y-6">
            <InfoRow
              icon={MapPin}
              label={t.contact.info.address_label}
              value={t.contact.info.address_value}
              testid="contact-info-address"
            />
            <InfoRow
              icon={Clock}
              label={t.contact.info.hours_label}
              value={t.contact.info.hours_value}
              testid="contact-info-hours"
            />
            <InfoRow
              icon={Phone}
              label={t.contact.info.phone_label}
              value="+39 334 808 4704"
              href="tel:+393348084704"
              testid="contact-info-phone"
            />
            <InfoRow
              icon={Mail}
              label={t.contact.info.email_label}
              value="info@proscenio.solutions"
              href="mailto:info@proscenio.solutions"
              testid="contact-info-email"
            />
          </div>
        </div>

        {/* Form */}
        <div className="md:col-span-7 md:pl-8 md:border-l md:border-white/10">
          <form
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8"
            noValidate
          >
            <div className="md:col-span-1">
              <label className="block text-[10px] tracking-[0.28em] uppercase text-zinc-500 mb-1">
                {t.contact.form.name} *
              </label>
              <input
                type="text"
                value={form.name}
                onChange={setField("name")}
                className="ps-input"
                required
                data-testid="contact-input-name"
              />
            </div>
            <div className="md:col-span-1">
              <label className="block text-[10px] tracking-[0.28em] uppercase text-zinc-500 mb-1">
                {t.contact.form.email} *
              </label>
              <input
                type="email"
                value={form.email}
                onChange={setField("email")}
                className="ps-input"
                required
                data-testid="contact-input-email"
              />
            </div>
            <div className="md:col-span-1">
              <label className="block text-[10px] tracking-[0.28em] uppercase text-zinc-500 mb-1">
                {t.contact.form.phone}
              </label>
              <input
                type="tel"
                value={form.phone}
                onChange={setField("phone")}
                className="ps-input"
                data-testid="contact-input-phone"
              />
            </div>
            <div className="md:col-span-1">
              <label className="block text-[10px] tracking-[0.28em] uppercase text-zinc-500 mb-1">
                {t.contact.form.area_label}
              </label>
              <select
                value={form.area}
                onChange={setField("area")}
                className="ps-input appearance-none pr-6 cursor-pointer"
                data-testid="contact-select-area"
                style={{ backgroundImage: "none" }}
              >
                {Object.entries(areas).map(([k, v]) => (
                  <option key={k} value={k} className="bg-[#0a0a0c] text-white">
                    {v}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-[10px] tracking-[0.28em] uppercase text-zinc-500 mb-1">
                {t.contact.form.subject}
              </label>
              <input
                type="text"
                value={form.subject}
                onChange={setField("subject")}
                className="ps-input"
                data-testid="contact-input-subject"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-[10px] tracking-[0.28em] uppercase text-zinc-500 mb-1">
                {t.contact.form.message} *
              </label>
              <textarea
                value={form.message}
                onChange={setField("message")}
                className="ps-input"
                rows={5}
                required
                data-testid="contact-input-message"
              />
            </div>
            <div className="md:col-span-2 mt-2">
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="ps-btn ps-btn-filled w-full md:w-auto"
              >
                {loading ? t.contact.form.sending : t.contact.form.submit}
                <Send className="w-4 h-4" strokeWidth={1.5} />
              </button>
              <p className="mt-4 text-xs text-zinc-500">
                {lang === "it"
                  ? "Inviando questo modulo accetti di essere ricontattato all'email indicata."
                  : "By submitting this form you agree to be contacted at the provided email."}
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

const InfoRow = ({ icon: Icon, label, value, href, testid }) => {
  const Content = (
    <div className="flex items-start gap-4 group" data-testid={testid}>
      <span className="mt-1 w-9 h-9 border border-white/15 flex items-center justify-center group-hover:border-[var(--ps-gold)] transition-colors duration-300">
        <Icon className="w-4 h-4 text-[var(--ps-gold)]" strokeWidth={1.4} />
      </span>
      <div>
        <div className="text-[10px] tracking-[0.28em] uppercase text-zinc-500 mb-1">{label}</div>
        <div className="text-base text-zinc-200">{value}</div>
      </div>
    </div>
  );
  if (href) {
    return (
      <a href={href} className="block hover:opacity-90 transition">
        {Content}
      </a>
    );
  }
  return Content;
};

export default Contact;
